import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Section } from "@/components/Section";
import { supabase } from "@/integrations/supabase/client";
import {
  getPostForEdit,
  savePost,
  isAdmin,
  searchPexels,
  uploadPostImage,
  ARTICLE_TYPES,
} from "@/lib/admin.functions";
import { listTeam } from "@/lib/content.functions";

export const Route = createFileRoute("/admin/post/$id")({
  ssr: false,
  beforeLoad: async () => {
    const { redirect } = await import("@tanstack/react-router");
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/auth" });
  },
  head: () => ({
    meta: [
      { title: "Editar post | Concurso Astor" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PostEditor,
});

const ARTICLE_TYPE_LABELS: Record<(typeof ARTICLE_TYPES)[number], string> = {
  info: "Informacional",
  review: "Review de produto",
  comparativo: "Comparativo",
  melhores: "Lista de melhores",
  empresa: "Empresa (perfil / NAP)",
};

const MARKETPLACES = ["Amazon", "Mercado Livre", "Magazine Luiza", "Shopee", "AliExpress", "Outro"];

type FormState = {
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  category: string;
  cover: string;
  body: string;
  author: string;
  reading_time: string;
  published: boolean;
  cover_image: string;
  youtube_urls: string;
  keywords: string;
  article_type: (typeof ARTICLE_TYPES)[number];
  product_name: string;
  product_image: string;
  product_image_alt: string;
  product_url: string;
  product_price: string;
  product_rating: string;
  product_reviews_count: string;
  product_features: string; // one per line
  product_cta_label: string;
  product_marketplace: string;
  product_cta_bg_color: string;
  product_cta_text_color: string;
};

const empty: FormState = {
  slug: "",
  title: "",
  description: "",
  excerpt: "",
  category: "Reviews",
  cover: "linear-gradient(135deg,#005BFF,#0a1a3a)",
  body: "",
  author: "Rafael Santos",
  reading_time: "5 min",
  published: false,
  cover_image: "",
  youtube_urls: "",
  keywords: "",
  article_type: "info",
  product_name: "",
  product_image: "",
  product_image_alt: "",
  product_url: "",
  product_price: "",
  product_rating: "",
  product_reviews_count: "",
  product_features: "",
  product_cta_label: "",
  product_marketplace: "",
  product_cta_bg_color: "#FF6A00",
  product_cta_text_color: "#FFFFFF",
};

function PostEditor() {
  const { id } = useParams({ from: "/admin/post/$id" });
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState<FormState>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) {
        navigate({ to: "/auth", replace: true });
        return;
      }
      try {
        const a = await isAdmin();
        if (!a.admin) {
          setAllowed(false);
          setLoading(false);
          return;
        }
        setAllowed(true);
        if (!isNew) {
          const post = (await getPostForEdit({ data: { id } })) as any;
          if (post) {
            setForm({
              slug: post.slug, title: post.title, description: post.description,
              excerpt: post.excerpt ?? "", category: post.category,
              cover: post.cover ?? empty.cover, body: post.body ?? "",
              author: post.author ?? empty.author, reading_time: post.reading_time ?? "5 min",
              published: Boolean(post.published),
              cover_image: post.cover_image ?? "",
              youtube_urls: (post.youtube_urls ?? []).join("\n"),
              keywords: (post.keywords ?? []).join(", "),
              article_type: (post.article_type ?? "info") as (typeof ARTICLE_TYPES)[number],
              product_name: post.product_name ?? "",
              product_image: post.product_image ?? "",
              product_image_alt: post.product_image_alt ?? "",
              product_url: post.product_url ?? "",
              product_price: post.product_price ?? "",
              product_rating: post.product_rating != null ? String(post.product_rating) : "",
              product_reviews_count:
                post.product_reviews_count != null ? String(post.product_reviews_count) : "",
              product_features: (post.product_features ?? []).join("\n"),
              product_cta_label: post.product_cta_label ?? "",
              product_marketplace: post.product_marketplace ?? "",
              product_cta_bg_color: post.product_cta_bg_color ?? "#FF6A00",
              product_cta_text_color: post.product_cta_text_color ?? "#FFFFFF",
            });
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function slugify(value: string) {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 120);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const ratingNum = form.product_rating.trim() ? Number(form.product_rating.replace(",", ".")) : null;
      const reviewsNum = form.product_reviews_count.trim()
        ? Math.trunc(Number(form.product_reviews_count.replace(/\D+/g, "")))
        : null;
      const payload = {
        ...form,
        cover_image: form.cover_image.trim() || null,
        youtube_urls: form.youtube_urls.split(/\s+/).map((s) => s.trim()).filter(Boolean),
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        product_name: form.product_name.trim() || null,
        product_image: form.product_image.trim() || null,
        product_image_alt: form.product_image_alt.trim() || null,
        product_url: form.product_url.trim() || null,
        product_price: form.product_price.trim() || null,
        product_rating: ratingNum != null && Number.isFinite(ratingNum) ? ratingNum : null,
        product_reviews_count:
          reviewsNum != null && Number.isFinite(reviewsNum) ? reviewsNum : null,
        product_features: form.product_features
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        product_cta_label: form.product_cta_label.trim() || null,
        product_marketplace: form.product_marketplace.trim() || null,
        product_cta_bg_color: form.product_cta_bg_color.trim() || null,
        product_cta_text_color: form.product_cta_text_color.trim() || null,
        ...(isNew ? {} : { id }),
      };
      await savePost({ data: payload });
      navigate({ to: "/admin" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  // Pexels search
  const [pexelsQuery, setPexelsQuery] = useState("");
  const [pexelsResults, setPexelsResults] = useState<Array<{ url: string; photographer: string }>>([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [team, setTeam] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    listTeam().then((rows) => setTeam(rows.map((r) => ({ id: r.id, name: r.name })))).catch(() => {});
  }, []);
  async function runPexelsSearch() {
    if (!pexelsQuery.trim()) return;
    setPexelsLoading(true);
    try {
      const r = await searchPexels({ data: { query: pexelsQuery.trim() } });
      setPexelsResults(r as any);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Pexels falhou");
    } finally {
      setPexelsLoading(false);
    }
  }

  // ───── Body editing helpers ─────
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  function insertAtCursor(text: string) {
    const el = bodyRef.current;
    if (!el) {
      update("body", form.body + text);
      return;
    }
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    const next = form.body.slice(0, start) + text + form.body.slice(end);
    update("body", next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function insertNofollowLink() {
    const el = bodyRef.current;
    const selected = el ? form.body.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0) : "";
    const anchor = (window.prompt("Texto do link:", selected) || "").trim();
    if (!anchor) return;
    const url = (window.prompt("URL (https://...)") || "").trim();
    if (!url) return;
    const html = `<a href="${url}" rel="nofollow noopener" target="_blank">${anchor}</a>`;
    if (el && (el.selectionEnd ?? 0) > (el.selectionStart ?? 0)) {
      const start = el.selectionStart!;
      const end = el.selectionEnd!;
      update("body", form.body.slice(0, start) + html + form.body.slice(end));
    } else {
      insertAtCursor(html);
    }
  }

  function insertProductCardToken() {
    insertAtCursor("\n\n{{PRODUCT_CARD}}\n\n");
  }

  function insertGoogleMap() {
    const raw = (window.prompt("Cole o iframe ou a URL de incorporação do Google Maps:") || "").trim();
    if (!raw) return;
    let iframe = raw;
    if (!/^<iframe/i.test(raw)) {
      iframe = `<iframe src="${raw}" width="100%" height="420" style="border:0" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>`;
    }
    const block = `\n\n<div class="map-embed rounded-2xl overflow-hidden border border-border my-4">${iframe}</div>\n\n`;
    insertAtCursor(block);
  }

  const [uploadingImg, setUploadingImg] = useState<"body" | "cover" | "product" | null>(null);
  async function handleLocalImageUpload(file: File, target: "body" | "cover" | "product") {
    if (!file) return;
    setUploadingImg(target);
    try {
      const buf = await file.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const base64 = btoa(bin);
      const r = await uploadPostImage({ data: {
        fileName: file.name, contentType: file.type || "image/jpeg", base64,
      } });
      if (target === "cover") update("cover_image", r.url);
      else if (target === "product") update("product_image", r.url);
      else insertAtCursor(`\n\n![${file.name}](${r.url})\n\n`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha no upload");
    } finally {
      setUploadingImg(null);
    }
  }

  if (loading) return <Section><p className="text-sm text-muted-foreground">Carregando...</p></Section>;
  if (!allowed) return <Section><p className="text-sm text-destructive">Sem permissão.</p></Section>;

  return (
    <Section>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{isNew ? "Novo post" : "Editar post"}</h1>
        <button type="button" onClick={() => navigate({ to: "/admin" })} className="text-sm text-muted-foreground hover:text-foreground">← Voltar</button>
      </div>

      <form onSubmit={onSubmit} className="card-glass space-y-4">
        <div>
          <label className="text-sm font-medium">Título</label>
          <input
            required value={form.title}
            onChange={(e) => {
              update("title", e.target.value);
              if (isNew && !form.slug) update("slug", slugify(e.target.value));
            }}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Slug</label>
            <input required value={form.slug} onChange={(e) => update("slug", slugify(e.target.value))}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-sm font-medium">Categoria</label>
            <input required value={form.category} onChange={(e) => update("category", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Tipo de artigo</label>
          <select
            value={form.article_type}
            onChange={(e) => update("article_type", e.target.value as (typeof ARTICLE_TYPES)[number])}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm"
          >
            {ARTICLE_TYPES.map((t) => (
              <option key={t} value={t}>{ARTICLE_TYPE_LABELS[t]}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            Define como o agente de IA estrutura o conteúdo (review, comparativo, lista de melhores ou informacional).
          </p>
        </div>
        <div>
          <label className="text-sm font-medium">Meta description (SEO)</label>
          <textarea required maxLength={300} rows={2} value={form.description}
            onChange={(e) => update("description", e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium">Resumo (excerpt)</label>
          <textarea rows={2} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-medium">Autor</label>
            <select value={form.author} onChange={(e) => update("author", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
              {team.map((m) => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
              {form.author &&
                !team.some((m) => m.name === form.author) && (
                  <option value={form.author}>{form.author}</option>
                )}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Tempo de leitura</label>
            <input value={form.reading_time} onChange={(e) => update("reading_time", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium">Capa (URL ou CSS gradient)</label>
            <input value={form.cover} onChange={(e) => update("cover", e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Conteúdo (parágrafos separados por linha em branco)</label>
          <div className="mt-1 mb-2 flex flex-wrap gap-2">
            <button type="button" onClick={insertNofollowLink}
              className="rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              🔗 Inserir link (nofollow)
            </button>
            <button type="button" onClick={insertProductCardToken}
              className="rounded-lg border border-primary/60 bg-primary/10 px-3 py-1.5 text-xs text-primary hover:bg-primary/20">
              🛒 Inserir card do produto
            </button>
            <button type="button" onClick={insertGoogleMap}
              className="rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              🗺️ Inserir Google Maps
            </button>
            <label className="cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              {uploadingImg === "body" ? "Enviando..." : "🖼️ Upload de imagem no corpo"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLocalImageUpload(f, "body");
                  e.currentTarget.value = "";
                }} />
            </label>
          </div>
          <textarea ref={bodyRef} rows={14} value={form.body} onChange={(e) => update("body", e.target.value)}
            className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          <p className="mt-1 text-xs text-muted-foreground">
            Suporta Markdown e HTML. Use <code>{"{{PRODUCT_CARD}}"}</code> onde quiser exibir o card de afiliado.
          </p>
        </div>

        {/* ───────── Produto afiliado ───────── */}
        <div className="space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Produto afiliado</h2>
              <p className="text-xs text-muted-foreground">
                Será exibido como card dentro do artigo (no marcador <code>{"{{PRODUCT_CARD}}"}</code> ou ao final).
              </p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Nome do produto</label>
              <input value={form.product_name} onChange={(e) => update("product_name", e.target.value)}
                placeholder="Ex.: Roborock Q7 Max+"
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Marketplace</label>
              <select value={form.product_marketplace} onChange={(e) => update("product_marketplace", e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm">
                <option value="">—</option>
                {MARKETPLACES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Link de afiliado</label>
              <input value={form.product_url} onChange={(e) => update("product_url", e.target.value)}
                placeholder="https://..."
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-sm font-medium">Preço (texto)</label>
              <input value={form.product_price} onChange={(e) => update("product_price", e.target.value)}
                placeholder="R$ 2.499,00"
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Texto do botão (CTA)</label>
              <input value={form.product_cta_label} onChange={(e) => update("product_cta_label", e.target.value)}
                placeholder="Ver oferta na Amazon"
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Nota (0–5)</label>
              <input type="number" min={0} max={5} step="0.1"
                value={form.product_rating} onChange={(e) => update("product_rating", e.target.value)}
                placeholder="4.7"
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Quantidade de depoimentos / avaliações</label>
              <input type="number" min={0} step="1"
                value={form.product_reviews_count} onChange={(e) => update("product_reviews_count", e.target.value)}
                placeholder="1280"
                className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Características (uma por linha)</label>
            <textarea rows={5} value={form.product_features} onChange={(e) => update("product_features", e.target.value)}
              placeholder={"Mapeamento a laser LiDAR\nSucção de 4200 Pa\nEstação de auto-esvaziamento"}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>

          <div className="grid gap-3 rounded-lg border border-border bg-background/40 p-3 sm:grid-cols-[1fr_1fr_auto]">
            <label className="flex flex-col gap-1 text-xs font-semibold text-foreground">
              Cor de fundo do botão CTA
              <div className="flex items-center gap-2">
                <input type="color" value={form.product_cta_bg_color} onChange={(e) => update("product_cta_bg_color", e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-border bg-background" />
                <input value={form.product_cta_bg_color} onChange={(e) => update("product_cta_bg_color", e.target.value)}
                  placeholder="#FF6A00"
                  className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              </div>
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold text-foreground">
              Cor do texto do botão CTA
              <div className="flex items-center gap-2">
                <input type="color" value={form.product_cta_text_color} onChange={(e) => update("product_cta_text_color", e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-border bg-background" />
                <input value={form.product_cta_text_color} onChange={(e) => update("product_cta_text_color", e.target.value)}
                  placeholder="#FFFFFF"
                  className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
              </div>
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-foreground">Prévia do botão</span>
              <span
                className="inline-flex items-center justify-center rounded-full px-5 py-2 text-xs font-extrabold uppercase tracking-wider"
                style={{
                  background: `linear-gradient(135deg, ${form.product_cta_bg_color || "#FF6A00"}, color-mix(in oklab, ${form.product_cta_bg_color || "#FF6A00"} 70%, #000))`,
                  color: form.product_cta_text_color || "#FFFFFF",
                }}
              >
                {form.product_cta_label.trim() || "Comprar agora"}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Imagem do produto</label>
            <input value={form.product_image} onChange={(e) => update("product_image", e.target.value)}
              placeholder="https://..."
              className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
            <div className="flex items-center gap-2">
              <label className="cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
                {uploadingImg === "product" ? "Enviando..." : "📤 Upload da imagem do produto"}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleLocalImageUpload(f, "product");
                    e.currentTarget.value = "";
                  }} />
              </label>
              <span className="text-[11px] text-muted-foreground">PNG/JPG/WebP até ~10 MB.</span>
            </div>
            {form.product_image && (
              <img src={form.product_image} alt={form.product_image_alt || form.product_name || "Produto"} className="mt-2 h-32 w-32 rounded-lg object-cover border border-border" loading="lazy" />
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Alt text da imagem do produto (SEO / acessibilidade)</label>
            <input value={form.product_image_alt} onChange={(e) => update("product_image_alt", e.target.value)}
              placeholder="Ex.: Robô aspirador Roborock Q5 Pro visto de cima"
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <p className="mt-1 text-xs text-muted-foreground">Descreva a imagem em uma frase. Aparece para leitores de tela e ajuda no Google Imagens.</p>
          </div>
        </div>

        <div className="space-y-2 rounded-xl border border-border p-4">
          <label className="text-sm font-medium">Imagem de capa (URL)</label>
          <input value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)}
            placeholder="https://..."
            className="w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
          <div className="flex items-center gap-2">
            <label className="cursor-pointer rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs hover:border-primary">
              {uploadingImg === "cover" ? "Enviando..." : "📤 Upload local de capa"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleLocalImageUpload(f, "cover");
                  e.currentTarget.value = "";
                }} />
            </label>
            <span className="text-[11px] text-muted-foreground">PNG/JPG/WebP até ~10 MB.</span>
          </div>
          {form.cover_image && (
            <img src={form.cover_image} alt="Capa" className="mt-2 h-32 w-full rounded-lg object-cover" loading="lazy" />
          )}
          <div className="mt-3 flex gap-2">
            <input value={pexelsQuery} onChange={(e) => setPexelsQuery(e.target.value)}
              placeholder="Buscar imagem no Pexels..."
              className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
            <button type="button" onClick={runPexelsSearch} disabled={pexelsLoading}
              className="rounded-lg border border-border px-4 py-2 text-sm">
              {pexelsLoading ? "Buscando..." : "Buscar"}
            </button>
          </div>
          {pexelsResults.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {pexelsResults.map((p, i) => (
                <button key={i} type="button" onClick={() => update("cover_image", p.url)}
                  className={`overflow-hidden rounded-lg border-2 transition ${form.cover_image === p.url ? "border-primary" : "border-transparent hover:border-border"}`}>
                  <img src={p.url} alt={p.photographer} className="h-24 w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">URLs do YouTube (uma por linha)</label>
            <textarea rows={3} value={form.youtube_urls} onChange={(e) => update("youtube_urls", e.target.value)}
              placeholder={"https://youtu.be/...\nhttps://www.youtube.com/watch?v=..."}
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm font-mono" />
            <p className="mt-1 text-xs text-muted-foreground">Serão embutidos como vídeos ao final do artigo.</p>
          </div>
          <div>
            <label className="text-sm font-medium">Palavras-chave (separadas por vírgula)</label>
            <textarea rows={3} value={form.keywords} onChange={(e) => update("keywords", e.target.value)}
              placeholder="robô aspirador, roborock q7, xiaomi mi robot"
              className="mt-1 w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} />
          Publicar agora
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate({ to: "/admin" })} className="rounded-lg border border-border px-4 py-2 text-sm">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary">{saving ? "Salvando..." : "Salvar"}</button>
        </div>
      </form>
    </Section>
  );
}
